import { useEffect, useState, useRef } from 'react'
import { Toaster, toast } from 'react-hot-toast'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { UPDATE_ORGANISATION } from '@graphql/organisation/updateOrganisation'
import { GET_ORGANISATION_BY_NAME } from '@graphql/organisation/getOrganisationByName'
import {
  CREATE_ZAPIER_API_TOKEN,
  GET_ZAPIER_INTEGRATION_STATUS,
  REVOKE_ZAPIER_INTEGRATION,
} from '@graphql/organisation/zapier'
import Image from 'next/image'
import { Button } from '@components/inputs/Button'
import Box from '@components/base/Box'
import { FileInput } from '@components/inputs/FileInput'
import { ColorPicker } from '@components/inputs/ColorPicker'
import useFileUploader from '@libs/useFileUploader'
import TickIcon from '@public/images/TickIcon.svg'
import { useTheme } from '@libs/useTheme'
import { RadioFieldInput } from '@components/inputs/RadioFieldInput'
import { useBlackBaud } from '@libs/useBlackBaud'
import { useAuth } from '@libs/Auth/useAuth'
import { useRouter } from 'next/router'
import Modal from '@components/base/Modal'
import {
  BiCheckCircle,
  BiCog,
  BiCopy,
  BiErrorCircle,
  BiPlug,
  BiRightArrowAlt,
} from 'react-icons/bi'
import { GET_IS_MERCHANT } from '@graphql/users/getMerchantAccount'
import { ErrorType } from '@libs/ErrorHandler/error.type'
import { isGoCardlessEnabled } from '@libs/paymentFeatureFlags'
import { Tab, Tabs } from '@components/base/Tabs'
import SideNav from '@components/base/SideNav'
import { User } from '@graphql/__generated/graphql'
import {
  MemberManager,
  OrderManager,
  OrganiserManager,
} from '@components/order-manager'
import { MembershipSettings } from '../MembershipSettings/MembershipSettings'
import useForummRouter, {
  ForummRouterEvents,
} from '@libs/Router/useForummRouter'
import {
  InitialiseOrganisationContext,
  OrganisationContext,
} from '@libs/Router/OrganisationContext'
import { getStripeConnectStatus } from '@libs/Stripe/util'
import {
  CREATE_PAYMENT_PROCESSOR_CONNECT_LINK,
  DISCONNECT_PAYMENT_PROCESSOR,
  GET_PAYMENT_PROCESSOR_DISCONNECT_IMPACT,
  GET_PAYMENT_SETTINGS,
} from '@graphql/payment/paymentSettings'

enum OrganisationPageTabs {
  Settings,
  Payments,
  Membership,
  Members,
  Orders,
  Organisers,
}

const ZAPIER_PUBLIC_INVITE_LINK =
  'https://zapier.com/developer/public-invite/242575/81ee6956a1ccda1298c683c42b0ebe31/'

const StripeStatusRow = ({
  label,
  complete,
}: {
  label: string
  complete: boolean
}) => (
  <Box className="flex items-center justify-between rounded-lg border border-forumm-menu-border px-4 py-3 text-sm">
    <Box className="font-medium">{label}</Box>
    <Box
      className={`flex items-center gap-2 font-semibold ${
        complete ? 'text-forumm-green' : 'text-forumm-orange'
      }`}
    >
      {complete ? (
        <BiCheckCircle className="h-5 w-5" />
      ) : (
        <BiErrorCircle className="h-5 w-5" />
      )}
      {complete ? 'Complete' : 'Required'}
    </Box>
  </Box>
)

export default function OrganisationSettings({ name }: { name: string }) {
  const goCardlessEnabled = isGoCardlessEnabled()
  const uploadFile = useFileUploader()
  const { initiateOAuth, onCallback, accessToken, completed } = useBlackBaud()

  const [updateOrganisation] = useMutation(UPDATE_ORGANISATION)
  const [logo, setLogo] = useState<string | File | undefined>()
  const [mainColour, setMainColour] = useState<string | undefined>()
  const [orgCurrency, setOrgcurrency] = useState<string>('GBP')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [updated, setUpdated] = useState<boolean>(false)
  const callbackExecutedRef = useRef(false)
  const { updateHighlightColour } = useTheme()
  const { isOrganizer, isAdmin, profile } = useAuth()
  const router = useRouter()
  const [routerModal, { setRouteError }] = useForummRouter()
  const orgctx = InitialiseOrganisationContext()

  if (!!router && !!router.events) {
    router.events.on('routeChangeStart', (url: string) => {
      if (orgctx.hasUnsavedChanges)
        setRouteError(ForummRouterEvents.MembershipPriceUnsaved, url)
    })
  }

  const [tab, setTab] = useState<OrganisationPageTabs>(
    OrganisationPageTabs.Settings
  )

  if (!isOrganizer && !isAdmin) {
    router.push('/')
  }

  const { data: organisationData } = useQuery(GET_ORGANISATION_BY_NAME, {
    variables: {
      name,
    },
    skip: !name,
  })

  useEffect(() => {
    if (organisationData?.getOrganisationByName) {
      const {
        mainColour: currentMainColour,
        logoImage,
        currency,
      } = organisationData?.getOrganisationByName
      if (logoImage) setLogo(logoImage)
      if (currentMainColour) setMainColour(currentMainColour)
      if (currency) setOrgcurrency(currency)
    }
  }, [organisationData])

  useEffect(() => {
    // Check if the URL contains the OAuth callback parameters
    if (
      window.location.search.includes('code') &&
      window.location.search.includes('state')
    ) {
      if (callbackExecutedRef.current) return
      callbackExecutedRef.current = true
      const lastEventEdited = window.localStorage.getItem(
        'Forumm_LastEventEdited'
      )
      const onCompleteAction = () => {
        if (lastEventEdited) {
          window.localStorage.removeItem('Forumm_LastEventEdited')
          router.push(`/create-event?id=${lastEventEdited}`)
        }
      }
      const msg = lastEventEdited
        ? 'All set! Heading back to your event…'
        : 'Your account was linked!'
      toast(msg, {
        icon: '🎉',
        duration: 3000,
      })
      onCallback(window.location.href, name, onCompleteAction)
    }
  }, [onCallback, name])

  const handleUpdateOrganisation = async () => {
    const variables = {
      name,
      mainColour,
      logoImage: logo,
      currency: orgCurrency,
    } as {
      name: string
      mainColour?: string
      logoImage?: string
      currency?: string
    }
    if (typeof logo === 'string') {
      variables.logoImage = logo
    }
    setIsLoading(true)
    await updateOrganisation({
      variables,
    })
    setIsLoading(false)
    if (variables.mainColour !== undefined && updateHighlightColour) {
      updateHighlightColour(variables?.mainColour)
    }

    setUpdated(true)
    setTimeout(() => {
      setUpdated(false)
    }, 3000)
  }

  const handleBlackBaud = () => {
    initiateOAuth()
  }

  const publicUrl =
    window.location.origin +
    '/org/' +
    organisationData?.getOrganisationByName?.url
  const previewLogo = typeof logo === 'string' ? logo : '/images/ForummLogo.svg'
  const connectedToBlackBaud =
    completed || organisationData?.getOrganisationByName?.blackBaudIsSet

  const [errors, setErrors] = useState<ErrorType | undefined>(undefined)
  const { data: merchantQueryData, refetch: refetchMerchant } = useQuery(GET_IS_MERCHANT, {
    variables: {
      input: { userId: profile?.userId, company: name },
    },
    skip: !profile?.userId,
  })

  const merchantData = getStripeConnectStatus(
    merchantQueryData?.getMerchantAccount
  )

  const {
    data: paymentSettingsData,
    loading: paymentSettingsLoading,
    refetch: refetchPaymentSettings,
  } = useQuery(GET_PAYMENT_SETTINGS, { skip: !profile?.userId })
  const [createPaymentProcessorConnectLink] = useMutation(
    CREATE_PAYMENT_PROCESSOR_CONNECT_LINK
  )
  const [getDisconnectImpact] = useLazyQuery(
    GET_PAYMENT_PROCESSOR_DISCONNECT_IMPACT,
    { fetchPolicy: 'network-only' }
  )
  const [disconnectPaymentProcessor] = useMutation(
    DISCONNECT_PAYMENT_PROCESSOR
  )

  const { query } = useRouter()

  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showStripeModal, setShowStripeModal] = useState(false)
  const [isLinkButtonLoading, setLinkButtonLoading] = useState(false)
  const [paymentActionLoading, setPaymentActionLoading] = useState<
    'STRIPE' | 'GOCARDLESS' | undefined
  >()
  const [zapierToken, setZapierToken] = useState<string | undefined>()
  const [zapierActionLoading, setZapierActionLoading] = useState(false)
  const [createZapierApiToken] = useMutation(CREATE_ZAPIER_API_TOKEN)
  const [revokeZapierIntegration] = useMutation(REVOKE_ZAPIER_INTEGRATION)
  const {
    data: zapierStatusData,
    loading: zapierStatusLoading,
    refetch: refetchZapierStatus,
  } = useQuery(GET_ZAPIER_INTEGRATION_STATUS, {
    variables: { name },
    skip: !name,
  })

  const zapierStatus = zapierStatusData?.getZapierIntegrationStatus

  const navItems = Object.entries(OrganisationPageTabs)
    .map((k) => ({
      label: k[0],
      targetStage: k[1] as OrganisationPageTabs,
    }))
    .filter((k) => typeof k.targetStage === 'number')

  const handlePaymentConnect = async (
    e: React.MouseEvent,
    processor: 'STRIPE' | 'GOCARDLESS'
  ) => {
    e.preventDefault()
    setLinkButtonLoading(true)
    setPaymentActionLoading(processor)

    if (!profile?.userId)
      throw new Error('You must be logged in to create a merchant account.')

    await createPaymentProcessorConnectLink({ variables: { processor } })
      .then((data) => {
        if (data.errors) throw data.errors
        window.location.href =
          data.data?.createPaymentProcessorConnectLink.redirectUrl!
        setLinkButtonLoading(false)
        setPaymentActionLoading(undefined)
      })
      .catch((err) => {
        setErrors({ message: err?.[0]?.message ?? err?.message })
        setLinkButtonLoading(false)
        setPaymentActionLoading(undefined)
      })
  }

  const handleStripeConnect = (e: React.MouseEvent) =>
    handlePaymentConnect(e, 'STRIPE')

  const handleDisconnectPaymentProcessor = async (
    processor: 'STRIPE' | 'GOCARDLESS'
  ) => {
    setPaymentActionLoading(processor)
    try {
      const impactResponse = await getDisconnectImpact({
        variables: { processor },
      })
      const impacted =
        impactResponse.data?.getPaymentProcessorDisconnectImpact ?? []
      const eventSummary = impacted.length
        ? `\n\nAffected live events:\n${impacted
            .map((event: { title: string }) => `- ${event.title}`)
            .join('\n')}`
        : '\n\nNo live events currently use this processor.'
      if (
        !window.confirm(
          `Disconnect ${processor === 'STRIPE' ? 'Stripe' : 'GoCardless'}? Active events will automatically switch to another ready processor, or have payments disabled.${eventSummary}`
        )
      )
        return

      await disconnectPaymentProcessor({ variables: { processor } })
      await Promise.all([refetchPaymentSettings(), refetchMerchant()])
      toast(`${processor === 'STRIPE' ? 'Stripe' : 'GoCardless'} disconnected.`, {
        icon: '✅',
      })
    } catch (error: any) {
      toast(error?.message ?? 'Unable to disconnect payment processor.', {
        icon: '❌',
      })
    } finally {
      setPaymentActionLoading(undefined)
    }
  }

  const handleCreateZapierToken = async () => {
    setZapierActionLoading(true)
    setZapierToken(undefined)
    try {
      const response = await createZapierApiToken({
        variables: { name },
      })
      const result = response.data?.createZapierApiToken
      if (!result?.success || !result?.token) {
        throw new Error(result?.error ?? 'Unable to create Zapier API key')
      }
      setZapierToken(result.token)
      await refetchZapierStatus()
      toast('Zapier API key created.', {
        icon: '✅',
        duration: 4000,
      })
    } catch (error: any) {
      toast(error?.message ?? 'Unable to create Zapier API key.', {
        icon: '❌',
        duration: 6000,
      })
    } finally {
      setZapierActionLoading(false)
    }
  }

  const handleRevokeZapier = async () => {
    setZapierActionLoading(true)
    try {
      const response = await revokeZapierIntegration({
        variables: { name },
      })
      if (!response.data?.revokeZapierIntegration?.success) {
        throw new Error('Unable to disconnect Zapier')
      }
      setZapierToken(undefined)
      await refetchZapierStatus()
      toast('Zapier disconnected.', {
        icon: '✅',
        duration: 4000,
      })
    } catch (error: any) {
      toast(error?.message ?? 'Unable to disconnect Zapier.', {
        icon: '❌',
        duration: 6000,
      })
    } finally {
      setZapierActionLoading(false)
    }
  }

  const handleCopyZapierToken = async () => {
    if (!zapierToken || !window.navigator?.clipboard) return
    await window.navigator.clipboard.writeText(zapierToken)
    toast('Zapier API key copied.', {
      icon: '✅',
      duration: 3000,
    })
  }

  const handleCopyZapierInviteLink = async () => {
    if (!window.navigator?.clipboard) return
    await window.navigator.clipboard.writeText(ZAPIER_PUBLIC_INVITE_LINK)
    toast('Zapier invite link copied.', {
      icon: '✅',
      duration: 3000,
    })
  }

  useEffect(() => {
    if (query.stripeconfirmed === 'true') setShowConfirmationModal(true)
  }, [query])

  useEffect(() => {
    if (query.tab === 'payments') setTab(OrganisationPageTabs.Payments)
  }, [query.tab])

  const successModal = (
    <Modal
      title="Stripe Connect Confirmation"
      show={showConfirmationModal}
      setShow={setShowConfirmationModal}
    >
      <Box className="flex flex-col mx-10 text-sm font-light">
        <Box className="flex flex-col items-center">
          <Box className="py-2 flex font-bold text-2xl">
            You have successfully linked your Stripe account.
          </Box>
        </Box>
      </Box>
    </Modal>
  )

  const stripeActionTitle = !merchantData.isMerchant
    ? 'Connect Stripe'
    : merchantData.isReady
      ? 'View Stripe status'
      : 'Continue Stripe setup'

  const stripeModal = (
    <Modal
      title="Stripe Settings"
      show={showStripeModal}
      setShow={setShowStripeModal}
      className="max-w-2xl"
    >
      <Box className="flex flex-col gap-5 px-2 pb-3 text-sm">
        <Box>
          <Box className="text-lg font-semibold">
            {merchantData.isReady
              ? 'Stripe is ready for payments'
              : 'Finish Stripe setup to accept payments'}
          </Box>
          <Box className="mt-2 opacity-80">
            Paid tickets can only be published once Stripe Connect is linked,
            onboarding is complete, and charges are enabled.
          </Box>
        </Box>
        <Box className="flex flex-col gap-3">
          <StripeStatusRow
            label="Stripe account created"
            complete={merchantData.isMerchant}
          />
          <StripeStatusRow
            label="Onboarding complete"
            complete={merchantData.onboardingComplete}
          />
          <StripeStatusRow
            label="Payments activated"
            complete={merchantData.chargesEnabled}
          />
        </Box>
        {!merchantData.isReady && (
          <Box className="flex flex-col gap-3 rounded-lg bg-forumm-blue-2/10 p-4 sm:flex-row sm:items-center sm:justify-between">
            <Box>
              <Box className="font-semibold">Action needed</Box>
              <Box className="mt-1 opacity-80">
                Continue to Stripe to complete the outstanding requirements.
              </Box>
            </Box>
            <Button
              title={stripeActionTitle}
              icon={<BiRightArrowAlt />}
              iconPos="end"
              buttonType="button"
              type="primary"
              loading={isLinkButtonLoading}
              onClick={handleStripeConnect}
            />
          </Box>
        )}
      </Box>
    </Modal>
  )

  const stripeConnectPanel = (
    <Box className="w-full max-w-md rounded-lg border border-forumm-menu-border p-5 shadow-sm">
      <Box className="flex items-start justify-between gap-4">
        <Box>
          <Box className="text-lg font-semibold">Stripe</Box>
          <Box className="mt-1 text-sm opacity-80">
            Link Stripe Connect before publishing events with paid tickets.
          </Box>
        </Box>
        <Box
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            merchantData.isReady
              ? 'bg-forumm-green/10 text-forumm-green'
              : 'bg-forumm-orange/10 text-forumm-orange'
          }`}
        >
          {merchantData.isReady ? 'Ready' : 'Setup required'}
        </Box>
      </Box>
      <Box className="mt-4 flex flex-col gap-2">
        <StripeStatusRow label="Account" complete={merchantData.isMerchant} />
        <StripeStatusRow
          label="Onboarding"
          complete={merchantData.onboardingComplete}
        />
        <StripeStatusRow
          label="Payments"
          complete={merchantData.chargesEnabled}
        />
      </Box>
      <Box className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Button
          title={stripeActionTitle}
          icon={<BiCog />}
          iconPos="end"
          buttonType="button"
          type={merchantData.isReady ? 'secondary' : 'primary'}
          loading={isLinkButtonLoading}
          onClick={(e) => {
            if (merchantData.isReady) {
              setShowStripeModal(true)
              return
            }
            handleStripeConnect(e)
          }}
        />
        <Button
          title="Details"
          buttonType="button"
          type="secondary"
          onClick={() => setShowStripeModal(true)}
        />
        {merchantData.isMerchant && (
          <Button
            title="Disconnect"
            buttonType="button"
            type="danger"
            loading={paymentActionLoading === 'STRIPE'}
            onClick={() => handleDisconnectPaymentProcessor('STRIPE')}
          />
        )}
      </Box>
    </Box>
  )

  const goCardlessSettings = paymentSettingsData?.getPaymentSettings?.gocardless
  const goCardlessConnectPanel = (
    <Box className="w-full max-w-md rounded-lg border border-forumm-menu-border p-5 shadow-sm">
      <Box className="flex items-start justify-between gap-4">
        <Box>
          <Box className="text-lg font-semibold">GoCardless</Box>
          <Box className="mt-1 text-sm opacity-80">
            Accept GBP and EUR paid-ticket purchases using Instant Bank Pay.
          </Box>
        </Box>
        <Box
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            goCardlessSettings?.ready
              ? 'bg-forumm-green/10 text-forumm-green'
              : 'bg-forumm-orange/10 text-forumm-orange'
          }`}
        >
          {paymentSettingsLoading
            ? 'Checking'
            : goCardlessSettings?.ready
              ? 'Ready'
              : 'Setup required'}
        </Box>
      </Box>
      {!!goCardlessSettings?.reason && (
        <Box className="mt-4 rounded-lg border border-forumm-menu-border px-4 py-3 text-sm opacity-80">
          {goCardlessSettings.reason}
        </Box>
      )}
      <Box className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Button
          title={
            goCardlessSettings?.configured
              ? 'Reconnect GoCardless'
              : 'Connect GoCardless'
          }
          icon={<BiPlug />}
          buttonType="button"
          type={goCardlessSettings?.ready ? 'secondary' : 'primary'}
          loading={paymentActionLoading === 'GOCARDLESS'}
          onClick={(event) => handlePaymentConnect(event, 'GOCARDLESS')}
        />
        {goCardlessSettings?.configured && (
          <Button
            title="Disconnect"
            buttonType="button"
            type="danger"
            loading={paymentActionLoading === 'GOCARDLESS'}
            onClick={() => handleDisconnectPaymentProcessor('GOCARDLESS')}
          />
        )}
      </Box>
    </Box>
  )

  const zapierStatusLabel = zapierStatusLoading
    ? 'Loading'
    : zapierStatus?.connected
      ? 'Connected'
      : zapierStatus?.hasApiToken
        ? 'API key active'
        : 'Not connected'
  const zapierStatusReady = !!zapierStatus?.hasApiToken

  const zapierConnectPanel = (
    <Box className="mt-6 w-full max-w-md rounded-lg border border-forumm-menu-border p-5 shadow-sm">
      <Box className="flex items-start justify-between gap-4">
        <Box>
          <Box className="text-lg font-semibold">Zapier</Box>
          <Box className="mt-1 text-sm opacity-80">
            Send registration and attendance updates to Zapier.
          </Box>
        </Box>
        <Box
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            zapierStatusReady
              ? 'bg-forumm-green/10 text-forumm-green'
              : 'bg-forumm-orange/10 text-forumm-orange'
          }`}
        >
          {zapierStatusLabel}
        </Box>
      </Box>

      {!!zapierStatus?.hasApiToken && !zapierToken && (
        <Box className="mt-4 rounded-lg border border-forumm-menu-border px-4 py-3 text-sm">
          <Box className="font-semibold">API key</Box>
          <Box className="mt-1 opacity-80">
            Ending in {zapierStatus.tokenLastFour ?? 'unknown'}
          </Box>
          <Box className="mt-1 opacity-80">
            Active Zapier hooks: {zapierStatus.activeSubscriptionCount ?? 0}
          </Box>
        </Box>
      )}

      {!!zapierToken && (
        <Box className="mt-4 rounded-lg border border-forumm-green/40 bg-forumm-green/10 px-4 py-3 text-sm">
          <Box className="font-semibold">New API key</Box>
          <Box className="mt-2 break-all font-mono text-xs">{zapierToken}</Box>
          <Button
            title="Copy"
            type="secondary"
            size="small"
            icon={<BiCopy />}
            className="mt-3"
            onClick={handleCopyZapierToken}
          />
        </Box>
      )}

      <Box className="mt-4 rounded-lg border border-forumm-menu-border px-4 py-3 text-sm">
        <Box className="font-semibold">Zapier app invite</Box>
        <Box className="mt-1 opacity-80">
          Use this invite link to add the Forumm Zapier integration to your
          Zaps.
        </Box>
        <Box className="mt-2 break-all font-mono text-xs">
          <a href={ZAPIER_PUBLIC_INVITE_LINK} rel="noreferrer" target="_blank">
            {ZAPIER_PUBLIC_INVITE_LINK}
          </a>
        </Box>
        <Button
          title="Copy invite link"
          type="secondary"
          size="small"
          icon={<BiCopy />}
          className="mt-3"
          onClick={handleCopyZapierInviteLink}
        />
      </Box>

      <Box className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Button
          title={
            zapierStatus?.hasApiToken ? 'Generate new key' : 'Generate API key'
          }
          icon={<BiPlug />}
          buttonType="button"
          type="primary"
          loading={zapierActionLoading || zapierStatusLoading}
          disabled={zapierActionLoading || zapierStatusLoading}
          onClick={handleCreateZapierToken}
        />
        {!!zapierStatus?.hasApiToken && (
          <Button
            title="Disconnect"
            buttonType="button"
            type="danger"
            loading={zapierActionLoading}
            disabled={zapierActionLoading}
            onClick={handleRevokeZapier}
            confirmationModal={{
              title: 'Disconnect Zapier',
              content:
                'This revokes all Zapier API keys and disables active Zapier hooks for this organisation.',
            }}
          />
        )}
      </Box>
    </Box>
  )

  const handleTabChange = (e: OrganisationPageTabs) => {
    if (orgctx.hasUnsavedChanges)
      return setRouteError(
        ForummRouterEvents.MembershipPriceUnsaved,
        `LOCAL`,
        e
      )
    setTab(e)
  }

  // const handleRouteChange = (event: ForummRouterEvents, tab: any) => {
  //   console.log({ event, tab })
  //   if (event === ForummRouterEvents.MembershipPriceUnsavedConfirmed && !!tab) {
  //     orgctx.setHasUnsavedChanges(false)
  //     setTab(tab)
  //   }
  // }
  //
  // router?.events?.on('routeChangeStart', handleRouteChange)

  return (
    <OrganisationContext.Provider value={orgctx}>
      <Box className="flex justify-center flex-col md:flex-row mb-10 mt-0 md:mt-20 w-full max-w-[1500px]">
        {routerModal}
        {successModal}
        {stripeModal}
        <SideNav
          className="justify-center xs:justify-start xs:ml-24 mb-4"
          navItems={navItems}
          stage={tab}
          setStage={handleTabChange}
        />
        <Tabs activeTitle={tab} className={`flex-1 px-4`}>
          <Tab title={OrganisationPageTabs.Orders}>
            <OrderManager />
          </Tab>
          <Tab title={OrganisationPageTabs.Membership}>
            <MembershipSettings />
          </Tab>
          <Tab title={OrganisationPageTabs.Members}>
            <MemberManager />
          </Tab>
          <Tab title={OrganisationPageTabs.Organisers}>
            <OrganiserManager className={`mb-10`} />
          </Tab>
          <Tab title={OrganisationPageTabs.Payments}>
            <Box className="flex flex-col items-center gap-6 mt-14 text-white w-full">
              <Box className="text-3xl">Payment Settings</Box>
              <Box className="text-sm opacity-80 max-w-2xl text-center">
                {goCardlessEnabled
                  ? 'Connect one or both processors. Each paid event uses exactly one processor at a time.'
                  : 'Connect Stripe before publishing events with paid tickets.'}
              </Box>
              {stripeConnectPanel}
              {goCardlessEnabled && goCardlessConnectPanel}
            </Box>
          </Tab>
          <Tab title={OrganisationPageTabs.Settings}>
            <Box className="flex flex-col items-center mt-14 text-white w-full">
              <Box className="text-3xl">Organisation Settings</Box>
              <Box className="pt-2 justify-center flex flex-col">
                <Box className="relative justify-center  mb-8">
                  <Image
                    src={previewLogo}
                    width={160}
                    height={160}
                    alt="Profile Image"
                    className="w-40 h-40 bg-contain bg-center rounded-full object-cover mt-8 ml-4"
                  />
                  <Box className="absolute top-10 right-2">
                    <FileInput
                      uploadFile={uploadFile}
                      value={logo}
                      onChange={(data) => {
                        setLogo(data)
                      }}
                      minified
                    />
                  </Box>
                </Box>
                <ColorPicker
                  className="font-medium"
                  value={mainColour}
                  label="Main Organisation Colour:"
                  onChange={(newColour) => {
                    setMainColour(newColour)
                  }}
                />
                <Box className="pb-2 mt-10">
                  <RadioFieldInput
                    label="Select currency:"
                    testid={'currency-radio'}
                    options={['USD', 'CAD', 'EUR', 'GBP']}
                    className="font-medium"
                    itemClassName="text-sm mt-4"
                    value={orgCurrency}
                    onChange={(currency) => {
                      setOrgcurrency(currency)
                    }}
                  />
                </Box>

                {zapierConnectPanel}
                <Box className="mt-10">
                  <p className="font-medium mb-1">Organisation Public URL:</p>
                  <p>
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#0052DD]"
                      href={publicUrl}
                    >
                      {publicUrl}
                    </a>
                  </p>
                </Box>
              </Box>
              {updated && (
                <Box
                  className={`transition-all !text-white p-4 flex items-center justify-center mt-4 bg-green-400 pointer-events-none rounded text-center`}
                >
                  Your organisation has been updated!
                  <TickIcon className="inline-block h-8 w-8 ml-2" />
                </Box>
              )}
              <Box className="max-w-md">
                {/* {connectedToBlackBaud && ( */}
                {/*   <span */}
                {/*     onClick={handleBlackBaud} */}
                {/*     className="w-full mt-6 text-center text-xs cursor-pointer" */}
                {/*   > */}
                {/*     <b>Connected to Blackbaud.</b> To keep your session secure, */}
                {/*     it may expire after some time. Please, use the button below */}
                {/*     to refresh your session when needed. */}
                {/*   </span> */}
                {/* )} */}
                {/* <Button */}
                {/*   type="secondary" */}
                {/*   loading={isLoading} */}
                {/*   onClick={handleBlackBaud} */}
                {/*   className="w-full mt-2" */}
                {/*   title={ */}
                {/*     !connectedToBlackBaud ? 'Connect to BlackBaud' : 'Reconnect' */}
                {/*   } */}
                {/* /> */}
                <Button
                  loading={isLoading}
                  onClick={handleUpdateOrganisation}
                  className="w-full mt-8"
                  title="Save Changes"
                />
              </Box>
            </Box>
          </Tab>
        </Tabs>
        <Toaster
          toastOptions={{
            className:
              'shadow-lg !rounded-full flex items-center border-2 !bg-forumm-white border-forumm-green !text-forumm-green mt-[80px]',
            duration: 4000,
          }}
        />
      </Box>
    </OrganisationContext.Provider>
  )
}
